import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Group, Select, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import AccountingReportFilters, {
  REPORT_FILTER_WIDTH,
} from "./accounting/AccountingReportFilters";
import GeneralLedgerView from "./accounting/GeneralLedgerView";
import { downloadGeneralLedgerCsv } from "./accounting/generalLedgerUtils";
import { defaultPeriodEndDate } from "./accounting/financialStatementsUtils";

const ReportGeneralLedger = () => {
  const [api] = useServerApi();
  const { t, i18n } = useTranslation();
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [accountCode, setAccountCode] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await api.Accounting.getChartOfAccountsTree();
        const list = Array.isArray(data) ? data : data?.accounts || [];
        setAccounts(
          list
            .filter((a) => a.isActive !== false && !a.isControlAccount)
            .map((a) => ({
              value: a.code,
              label: `${a.code} - ${a.name}`,
            }))
        );
      } catch (error) {
        setAccounts([]);
      }
    };
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = async (overrides = {}) => {
    if (!fiscalPeriodId) return;
    const effectiveStart = overrides.startDate ?? startDate;
    const effectiveEnd = overrides.endDate ?? endDate;

    setLoading(true);
    try {
      const query = { fiscalPeriodId };
      if (effectiveStart) query.startDate = effectiveStart.toISOString();
      if (effectiveEnd) query.endDate = effectiveEnd.toISOString();
      if (accountCode) query.accountCode = accountCode;
      const data = await api.Accounting.getGeneralLedger(query);
      setRows(
        (data?.rows || []).map((row, index) => ({
          ...row,
          _id: `${row.journalCode}-${row.accountCode}-${index}`,
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;

    const init = async () => {
      setLoading(true);
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
        const periodStart = period?.startDate ? new Date(period.startDate) : null;
        const periodEnd = defaultPeriodEndDate(period?.endDate);
        setStartDate(periodStart);
        setEndDate(periodEnd);

        const query = { fiscalPeriodId };
        if (periodStart) query.startDate = periodStart.toISOString();
        if (periodEnd) query.endDate = periodEnd.toISOString();
        if (accountCode) query.accountCode = accountCode;
        const data = await api.Accounting.getGeneralLedger(query);
        setRows(
          (data?.rows || []).map((row, index) => ({
            ...row,
            _id: `${row.journalCode}-${row.accountCode}-${index}`,
          }))
        );
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode]);

  const accountFilter = useMemo(
    () => (
      <div style={{ width: REPORT_FILTER_WIDTH }}>
        <Select
          size="sm"
          label={t("accounting.journal.account")}
          placeholder={t("accounting.financeReports.generalLedger.allAccounts")}
          data={accounts}
          value={accountCode}
          onChange={setAccountCode}
          searchable
          clearable
        />
      </div>
    ),
    [accountCode, accounts, t]
  );

  const handleDownload = useCallback(() => {
    if (rows.length === 0) {
      showNotification({
        color: "yellow",
        title: t("accounting.trialBalance.downloadFailed"),
        message: t("accounting.financeReports.generalLedger.noActivity"),
      });
      return;
    }
    downloadGeneralLedgerCsv({ rows, periodName, accountCode, t });
  }, [accountCode, periodName, rows, t]);

  return (
    <div>
      <Text size="xs" color="dimmed" mb="xs">
        {t("accounting.financeReports.generalLedger.intro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
        showPnlDateRange
        pnlStartDate={startDate}
        onPnlStartDateChange={setStartDate}
        pnlEndDate={endDate}
        onPnlEndDateChange={setEndDate}
      >
        {accountFilter}
      </AccountingReportFilters>
      <Group mb="sm">
        <Button
          size="xs"
          variant="default"
          onClick={handleDownload}
          disabled={rows.length === 0}
        >
          {t("Download")}
        </Button>
        <Button size="xs" onClick={() => loadReport()} loading={loading}>
          {t("Refresh")}
        </Button>
      </Group>
      <GeneralLedgerView
        rows={rows}
        loading={loading}
        i18nLanguage={i18n.language}
      />
    </div>
  );
};

export default ReportGeneralLedger;
